import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  useTheme, 
  Grid,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLayout } from '../../context/LayoutContext';
// Remove hardcoded icon imports since we're using registry now
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda'; // Keep one as default fallback
import tokens from '../../theme/tokens';
import CollapsibleSectionHeader from '../shared/CollapsibleSectionHeader';
import { useLanguage } from '../../localization';
import { getTranslatedLayoutName, getTranslatedLayoutCategory } from '../../layouts/LayoutRegistry';

const LayoutSelectorComponent = () => {
  const { t } = useLanguage();
  const { layouts, selectedLayout, setSelectedLayout } = useLayout();
  const theme = useTheme();

  // --- State for collapsible sections ---
  const [openLayoutCategories, setOpenLayoutCategories] = useState({
    'Classic': true, // Keep categories open by default
    'Sidebar': true,
    'Topbar': true,
    'Other': true, // Add 'Other' just in case
  });

  // --- Memoize the grouping of layouts by category --- 
  const layoutsByCategory = useMemo(() => {
    console.log('LayoutSelector - Recalculating layoutsByCategory'); // Debug log
    return (layouts || []).reduce((acc, layout) => {
      const category = layout.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(layout);
      return acc;
    }, {});
  }, [layouts]); // Dependency is the layouts array from context

  // --- Define category order ---
  const layoutCategoryOrder = ['Classic', 'Sidebar', 'Topbar', 'Other']; // Define the order

  // --- Toggle category collapse state ---
  const toggleLayoutCategory = (category) => {
    setOpenLayoutCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Updated to use registry information
  const getThumbnail = (layout) => {
    try {
      // Try to load the SVG from the registry-provided path
      if (layout.thumbnailPath) {
        const thumbnailPath = require(`../../assets/${layout.thumbnailPath}`);
        return <img src={thumbnailPath} alt={layout.name} style={{ 
          maxWidth: '100%', 
          maxHeight: '100%',
          objectFit: 'contain',
          filter: selectedLayout === layout.id ? 'none' : 'grayscale(0.3) opacity(0.8)',
          transition: 'all 0.3s ease'
        }} />;
      }
      throw new Error('No thumbnail path provided');
    } catch (e) {
      // Use fallback icon from registry or default
      if (layout.fallbackIcon) {
        const IconComponent = layout.fallbackIcon;
        // Apply any special props if specified, otherwise use default styling
        const iconProps = layout.fallbackIconProps || { 
          sx: { fontSize: 60, color: tokens.colors.text.secondary } 
        };
        return <IconComponent {...iconProps} />;
      }
      // Ultimate fallback (should rarely happen if registry is properly set up)
      return <ViewAgendaIcon sx={{ fontSize: 60, color: tokens.colors.text.secondary }} />;
    }
  };

  // Layout selection content
  return (
    <Box sx={{ p: tokens.spacing[3] }}>
      {layoutCategoryOrder.map(category => layoutsByCategory[category] && (
        <Box key={category} sx={{ mb: tokens.spacing[5] }}>
          {/* Use the shared component */}
          <CollapsibleSectionHeader
            title={getTranslatedLayoutCategory(category, t)}
            isOpen={!!openLayoutCategories[category]}
            toggleOpen={() => toggleLayoutCategory(category)}
            isCategory={true} // Apply category styling
          />

          <Collapse in={!!openLayoutCategories[category]}>
            {/* Layout Grid - More spacious and visual */}
            <Grid container spacing={tokens.spacing[4]} sx={{ pt: tokens.spacing[2], justifyContent: 'flex-start' }}>
              {layoutsByCategory[category].map((layout) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={layout.id}>
                  <Paper
                    elevation={0}
                    onClick={() => setSelectedLayout(layout.id)}
                    sx={{
                      cursor: 'pointer',
                      border: `2px solid ${selectedLayout === layout.id ? theme.palette.primary.main : 'transparent'}`,
                      borderRadius: tokens.radius.lg,
                      overflow: 'hidden',
                      transition: `all ${tokens.transitions.durations.standard} cubic-bezier(0.4, 0, 0.2, 1)`,
                      backgroundColor: selectedLayout === layout.id ? tokens.colors.primary[50] : tokens.colors.background.paper,
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-4px) scale(1.02)',
                        boxShadow: tokens.shadows.lg,
                        border: `2px solid ${theme.palette.primary.light}`,
                        '& .layout-thumbnail': {
                          filter: 'none !important',
                        },
                        '& .layout-name': {
                          color: theme.palette.primary.main,
                        }
                      },
                      '&:active': {
                        transform: 'translateY(-2px) scale(1.01)',
                      }
                    }}
                  >
                    {/* Selected indicator */}
                    {selectedLayout === layout.id && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: tokens.spacing[2],
                          right: tokens.spacing[2],
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2,
                          '&::after': {
                            content: '"✓"',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }
                        }}
                      />
                    )}

                    {/* Thumbnail Area - Much larger and more prominent */}
                    <Box
                      className="layout-thumbnail"
                      sx={{
                        height: '220px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white',
                        padding: tokens.spacing[3],
                        position: 'relative',
                      }}
                    >
                      {getThumbnail(layout)}
                    </Box>

                    {/* Minimal text area */}
                    <Box sx={{ 
                      p: tokens.spacing[3], 
                      pt: tokens.spacing[2],
                      textAlign: 'center',
                      backgroundColor: selectedLayout === layout.id ? tokens.colors.primary[50] : 'transparent'
                    }}>
                      <Typography 
                        className="layout-name"
                        component="h3" 
                        sx={{ 
                          fontWeight: tokens.typography.fontWeights.medium, 
                          fontSize: tokens.typography.fontSizes.sm, 
                          color: selectedLayout === layout.id ? theme.palette.primary.main : tokens.colors.text.primary,
                          transition: 'color 0.3s ease',
                          mb: 0
                        }}
                      >
                        {getTranslatedLayoutName(layout.name, t)}
                      </Typography>
                      {/* Remove description to focus on visuals */}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </Box>
      ))}
    </Box>
  );
}

const LayoutSelector = React.memo(LayoutSelectorComponent);

export default LayoutSelector; 